import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { getAuthenticatedAxios, handleAuthError } from '../utils/api';

const WarmupConfiguration = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get('/api/warmup/config');
      if (response.data.success) {
        setConfig(response.data.config);
      }
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to fetch configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  // Используем конфигурацию по умолчанию, если не загрузилась
  const defaultConfig = {
    enabled: false,
    use_blum: false,
    wallets_per_batch: 5,
    batch_interval_seconds: 600,
    swap_delay_min_seconds: 5,
    swap_delay_max_seconds: 15,
    wallet_delay_min_seconds: 1,
    wallet_delay_max_seconds: 3,
    max_concurrent_swaps: 3,
    min_wallet_balance_ton: 0.5,
    gas_reserve_ton: 0.1,
    enable_reverse_swap: true,
    reverse_swap_delay_seconds: 10,
    // BLUM settings
    blum_auto_sell: true,
    blum_sell_percent_min: 90,
    blum_sell_percent_max: 100
  };

  const displayConfig = config || defaultConfig;

  const handleSave = async () => {
    setSaving(true);
    try {
      const api = getAuthenticatedAxios();
      const configToSave = config || defaultConfig;
      await api.post('/api/warmup/config', configToSave);
      toast.success('Configuration saved');
      setConfig(configToSave);
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to save configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    const currentConfig = config || { ...defaultConfig };
    setConfig({ ...currentConfig, [field]: value });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Загрузка конфигурации...</div>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card>
      <CardHeader>
        <CardTitle>Warmup Configuration</CardTitle>
        <CardDescription>Configure warmup service parameters</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="enabled">Enable Warmup</Label>
          <Switch
            id="enabled"
            checked={displayConfig.enabled}
            onCheckedChange={(checked) => handleChange('enabled', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="use_blum">Использовать BLUM</Label>
            <p className="text-xs text-gray-500">Покупать/продавать токены через BLUM DEX</p>
          </div>
          <Switch
            id="use_blum"
            checked={displayConfig.use_blum}
            onCheckedChange={(checked) => handleChange('use_blum', checked)}
          />
        </div>

        {displayConfig.use_blum && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-purple-800">Настройки BLUM</h4>
            <div className="flex items-center justify-between">
              <Label htmlFor="blum_auto_sell">Авто-продажа после покупки</Label>
              <Switch
                id="blum_auto_sell"
                checked={displayConfig.blum_auto_sell}
                onCheckedChange={(checked) => handleChange('blum_auto_sell', checked)}
              />
            </div>
            {displayConfig.blum_auto_sell && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="blum_sell_percent_min">Мин. % продажи</Label>
                  <Input
                    id="blum_sell_percent_min"
                    type="number"
                    value={displayConfig.blum_sell_percent_min || 90}
                    onChange={(e) => handleChange('blum_sell_percent_min', parseInt(e.target.value))}
                    min="1"
                    max="100"
                  />
                </div>
                <div>
                  <Label htmlFor="blum_sell_percent_max">Макс. % продажи</Label>
                  <Input
                    id="blum_sell_percent_max"
                    type="number"
                    value={displayConfig.blum_sell_percent_max || 100}
                    onChange={(e) => handleChange('blum_sell_percent_max', parseInt(e.target.value))}
                    min="1"
                    max="100"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="wallets_per_batch">Wallets per Batch</Label>
            <Input
              id="wallets_per_batch"
              type="number"
              value={displayConfig.wallets_per_batch || 5}
              onChange={(e) => handleChange('wallets_per_batch', parseInt(e.target.value))}
              min="1"
              max="10"
            />
          </div>

          <div>
            <Label htmlFor="batch_interval_seconds">Batch Interval (seconds)</Label>
            <Input
              id="batch_interval_seconds"
              type="number"
              value={displayConfig.batch_interval_seconds || 600}
              onChange={(e) => handleChange('batch_interval_seconds', parseInt(e.target.value))}
              min="60"
              max="3600"
            />
          </div>

          <div>
            <Label htmlFor="swap_delay_min_seconds">Min Swap Delay (seconds)</Label>
            <Input
              id="swap_delay_min_seconds"
              type="number"
              value={displayConfig.swap_delay_min_seconds || 5}
              onChange={(e) => handleChange('swap_delay_min_seconds', parseInt(e.target.value))}
              min="0"
              max="300"
            />
          </div>

          <div>
            <Label htmlFor="swap_delay_max_seconds">Max Swap Delay (seconds)</Label>
            <Input
              id="swap_delay_max_seconds"
              type="number"
              value={displayConfig.swap_delay_max_seconds || 15}
              onChange={(e) => handleChange('swap_delay_max_seconds', parseInt(e.target.value))}
              min="0"
              max="300"
            />
          </div>

          <div>
            <Label htmlFor="wallet_delay_min_seconds">Min Wallet Delay (seconds)</Label>
            <Input
              id="wallet_delay_min_seconds"
              type="number"
              value={displayConfig.wallet_delay_min_seconds || 1}
              onChange={(e) => handleChange('wallet_delay_min_seconds', parseInt(e.target.value))}
              min="0"
              max="60"
            />
          </div>

          <div>
            <Label htmlFor="wallet_delay_max_seconds">Max Wallet Delay (seconds)</Label>
            <Input
              id="wallet_delay_max_seconds"
              type="number"
              value={displayConfig.wallet_delay_max_seconds || 3}
              onChange={(e) => handleChange('wallet_delay_max_seconds', parseInt(e.target.value))}
              min="0"
              max="60"
            />
          </div>

          <div>
            <Label htmlFor="max_concurrent_swaps">Max Concurrent Swaps</Label>
            <Input
              id="max_concurrent_swaps"
              type="number"
              value={displayConfig.max_concurrent_swaps || 3}
              onChange={(e) => handleChange('max_concurrent_swaps', parseInt(e.target.value))}
              min="1"
              max="10"
            />
          </div>

          <div>
            <Label htmlFor="min_wallet_balance_ton">Min Wallet Balance (TON)</Label>
            <Input
              id="min_wallet_balance_ton"
              type="number"
              value={displayConfig.min_wallet_balance_ton || 0.5}
              onChange={(e) => handleChange('min_wallet_balance_ton', parseFloat(e.target.value))}
              min="0.01"
              step="0.01"
            />
          </div>

          <div>
            <Label htmlFor="gas_reserve_ton">Gas Reserve (TON)</Label>
            <Input
              id="gas_reserve_ton"
              type="number"
              value={displayConfig.gas_reserve_ton || 0.1}
              onChange={(e) => handleChange('gas_reserve_ton', parseFloat(e.target.value))}
              min="0.1"
              step="0.1"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="enable_reverse_swap">Enable Reverse Swap</Label>
          <Switch
            id="enable_reverse_swap"
            checked={displayConfig.enable_reverse_swap}
            onCheckedChange={(checked) => handleChange('enable_reverse_swap', checked)}
          />
        </div>

        {displayConfig.enable_reverse_swap && (
          <div>
            <Label htmlFor="reverse_swap_delay_seconds">Reverse Swap Delay (seconds)</Label>
            <Input
              id="reverse_swap_delay_seconds"
              type="number"
              value={displayConfig.reverse_swap_delay_seconds || 10}
              onChange={(e) => handleChange('reverse_swap_delay_seconds', parseInt(e.target.value))}
              min="0"
              max="300"
            />
          </div>
        )}

        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default WarmupConfiguration;

